import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  IconButton,
  Chip,
  Avatar,
  LinearProgress,
  Divider
} from "@mui/material";
import {
  People as PeopleIcon,
  LocalHospital as DoctorIcon,
  Restaurant as DietIcon,
  Psychology as BrainIcon,
  Feedback as FeedbackIcon,
  QuestionAnswer as FAQIcon,
  FitnessCenter as YogaIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as ViewsIcon,
  ThumbUp as LikesIcon,
  Comment as CommentsIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  ExitToApp as ExitToAppIcon
} from "@mui/icons-material";
import { get } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import LineChart from "../../components/Chart/LineChart/LineChart";
import BarChart from "../../components/Chart/BarChart/BarChart";
import styles from "./Dashboard.module.css";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalDoctors: 0,
    totalDietPlans: 0,
    totalBrainSessions: 0,
    totalFeedbacks: 0,
    totalFAQs: 0,
    totalYogaVideos: 0,
    recentActivity: [],
    monthlyStats: {
      users: [],
      doctors: [],
      dietPlans: [],
      brainSessions: []
    },
    userEngagement: {
      activeUsers: 0,
      sessionDuration: 0,
      bounceRate: 0
    }
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch all dashboard data from the main stats API
      const res = await get('/api/dashboard/stats');
      const data = res?.data || {};
      setDashboardData({
        totalUsers: data.totalUsers || 0,
        totalDoctors: data.totalDoctors || 0,
        totalDietPlans: data.totalDietPlans || 0,
        totalBrainSessions: data.totalBrainSessions || 0,
        totalFeedbacks: data.totalFeedbacks || 0,
        totalFAQs: data.totalFAQs || 0,
        totalYogaVideos: data.totalYogaVideos || 0,
        recentActivity: data.recentActivity || [],
        monthlyStats: {
          users: data.monthlyStats?.users || [],
          doctors: data.monthlyStats?.doctors || [],
          dietPlans: data.monthlyStats?.dietPlans || [],
          brainSessions: data.monthlyStats?.brainSessions || []
        },
        userEngagement: data.userEngagement || { activeUsers: 0, sessionDuration: 0, bounceRate: 0 }
      });
      // Log the dashboard data for debugging
      console.log('Dashboard Data:', data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toastMessage("Error loading dashboard data", "error");
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, trend, trendValue }) => (
    <Card className={styles.statCard}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" component="div" fontWeight="bold" color={color}>
              {value.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              {title}
            </Typography>
            {trend && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend === "up" ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography 
                  variant="caption" 
                  color={trend === "up" ? "success.main" : "error.main"}
                  ml={0.5}
                >
                  {trendValue}% from last month
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const ActivityCard = ({ activity }) => (
    <Box className={styles.activityItem}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <Avatar 
            sx={{ 
              width: 32, 
              height: 32, 
              bgcolor: activity.type === "user" ? "#1976d2" : 
                      activity.type === "doctor" ? "#2e7d32" :
                      activity.type === "diet" ? "#ed6c02" : "#9c27b0"
            }}
          >
            {activity.type === "user" && <PeopleIcon />}
            {activity.type === "doctor" && <DoctorIcon />}
            {activity.type === "diet" && <DietIcon />}
            {activity.type === "feedback" && <FeedbackIcon />}
          </Avatar>
          <Box ml={2}>
            <Typography variant="body2" fontWeight="medium">
              {activity.message}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {activity.time}
            </Typography>
          </Box>
        </Box>
        <Chip 
          label={`+${activity.count}`} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      </Box>
    </Box>
  );

  const chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Users',
        data: dashboardData.monthlyStats?.users || [],
        borderColor: '#4bc0c0',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: '#4bc0c0',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: '#4bc0c0',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3,
        cubicInterpolationMode: 'monotone',
      },
      {
        label: 'Doctors',
        data: dashboardData.monthlyStats?.doctors || [],
        borderColor: '#ff6384',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: '#ff6384',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: '#ff6384',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3,
        cubicInterpolationMode: 'monotone',
      },
      {
        label: 'Diet Plans',
        data: dashboardData.monthlyStats?.dietPlans || [],
        borderColor: '#ffcd56',
        backgroundColor: 'rgba(255, 205, 86, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: '#ffcd56',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: '#ffcd56',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 3,
        cubicInterpolationMode: 'monotone',
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: '500',
          },
          color: '#333',
          boxWidth: 12,
          boxHeight: 12,
        },
        onClick: (e, legendItem) => {
          // Toggle dataset visibility on legend click
          const index = legendItem.datasetIndex;
          const chart = e.chart;
          const meta = chart.getDatasetMeta(index);
          meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
          chart.update();
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: function(context) {
            return `Month: ${context[0].label}`;
          },
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y || 0;
            return `${label}: ${value.toLocaleString()}`;
          },
          labelColor: function(context) {
            return {
              borderColor: context.dataset.borderColor,
              backgroundColor: context.dataset.borderColor,
            };
          },
        },
        animation: {
          duration: 200,
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            weight: '500',
          },
          color: '#666',
          padding: 10,
        },
      },
      y: {
        beginAtZero: true,
        display: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            weight: '500',
          },
          color: '#666',
          padding: 10,
          callback: function(value) {
            return value.toLocaleString();
          },
        },
        title: {
          display: true,
          text: 'Count',
          font: {
            size: 12,
            weight: '600',
          },
          color: '#333',
          padding: {
            top: 10,
            bottom: 10,
          },
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
    elements: {
      line: {
        borderJoinStyle: 'round',
      },
    },
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ width: '100%' }}>
          <LinearProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Dashboard Overview
        </Typography>
        
        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Active Patients"
              value={dashboardData.totalUsers}
              icon={<PeopleIcon />}
              color="#1976d2"
              trend="up"
              trendValue={12}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Doctors"
              value={dashboardData.totalDoctors}
              icon={<DoctorIcon />}
              color="#2e7d32"
              trend="up"
              trendValue={8}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Users Using Diet Plans"
              value={dashboardData.totalDietPlans}
              icon={<DietIcon />}
              color="#ed6c02"
              trend="up"
              trendValue={15}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Users Using CBT"
              value={dashboardData.totalBrainSessions}
              icon={<BrainIcon />}
              color="#9c27b0"
              trend="up"
              trendValue={20}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Feedbacks"
              value={dashboardData.totalFeedbacks}
              icon={<FeedbackIcon />}
              color="#d32f2f"
              trend="up"
              trendValue={5}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="FAQs"
              value={dashboardData.totalFAQs}
              icon={<FAQIcon />}
              color="#7b1fa2"
              trend="up"
              trendValue={10}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Yoga Modules"
              value={dashboardData.totalYogaVideos}
              icon={<YogaIcon />}
              color="#388e3c"
              trend="up"
              trendValue={25}
            />
          </Grid>
          {/* <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Views"
              value={12500}
              icon={<ViewsIcon />}
              color="#1976d2"
              trend="up"
              trendValue={18}
            />
          </Grid> */}
        </Grid>

        {/* Charts and Analytics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} lg={8}>
            <Card 
              className={styles.chartCard}
              sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                }
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <TrendingUpIcon sx={{ fontSize: 28, color: "#1976d2", mr: 1.5 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Growth Analytics
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Hover over data points for details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ height: 400, position: 'relative' }}>
                  <LineChart data={chartData} options={chartOptions} />
                </Box>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Total Users: ${(dashboardData.monthlyStats?.users || []).reduce((a, b) => a + b, 0).toLocaleString()}`}
                    size="small"
                    sx={{ bgcolor: 'rgba(75, 192, 192, 0.1)', color: '#4bc0c0', fontWeight: '500' }}
                  />
                  <Chip 
                    label={`Total Doctors: ${(dashboardData.monthlyStats?.doctors || []).reduce((a, b) => a + b, 0).toLocaleString()}`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255, 99, 132, 0.1)', color: '#ff6384', fontWeight: '500' }}
                  />
                  <Chip 
                    label={`Total Diet Plans: ${(dashboardData.monthlyStats?.dietPlans || []).reduce((a, b) => a + b, 0).toLocaleString()}`}
                    size="small"
                    sx={{ bgcolor: 'rgba(255, 205, 86, 0.1)', color: '#ffcd56', fontWeight: '500' }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} lg={4}>
            <Card className={styles.activityCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Activity
                </Typography>
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {dashboardData.recentActivity.map((activity, index) => (
                    <ActivityCard key={index} activity={activity} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Additional Analytics */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={styles.analyticsCard}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={3}>
                  <PeopleIcon sx={{ fontSize: 28, color: "#1976d2", mr: 1.5 }} />
                  <Typography variant="h6" fontWeight="bold">
                    User Engagement
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ mt: 2 }}>
                  {/* Active Users */}
                  <Box 
                    sx={{ 
                      mb: 3, 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "#e9ecef",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                      <Box display="flex" alignItems="center">
                        <GroupIcon sx={{ fontSize: 20, color: "#1976d2", mr: 1 }} />
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          Active Users
                        </Typography>
                      </Box>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        sx={{ 
                          color: "#1976d2",
                          fontSize: "1.5rem"
                        }}
                      >
                        {dashboardData.userEngagement.activeUsers}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                      Users active in the last 7 days
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min((dashboardData.userEngagement.activeUsers / Math.max(dashboardData.totalUsers, 1)) * 100, 100)} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: "#e3f2fd",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "#1976d2",
                          borderRadius: 4
                        }
                      }} 
                    />
                  </Box>
                  
                  {/* Session Duration */}
                  <Box 
                    sx={{ 
                      mb: 3, 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "#e9ecef",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                      <Box display="flex" alignItems="center">
                        <AccessTimeIcon sx={{ fontSize: 20, color: "#2e7d32", mr: 1 }} />
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          Session Duration
                        </Typography>
                      </Box>
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        sx={{ 
                          color: "#2e7d32",
                          fontSize: "1.5rem"
                        }}
                      >
                        {dashboardData.userEngagement.sessionDuration.toFixed(1)} min
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                      Average session duration from CBT sessions
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min((dashboardData.userEngagement.sessionDuration / 60) * 100, 100)} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: "#e8f5e9",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: "#2e7d32",
                          borderRadius: 4
                        }
                      }} 
                    />
                  </Box>
                  
                  {/* Bounce Rate */}
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "#e9ecef",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                      <Box display="flex" alignItems="center">
                        <ExitToAppIcon sx={{ fontSize: 20, color: dashboardData.userEngagement.bounceRate > 50 ? "#d32f2f" : "#ed6c02", mr: 1 }} />
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          Bounce Rate
                        </Typography>
                      </Box>
                      <Chip
                        label={`${dashboardData.userEngagement.bounceRate}%`}
                        sx={{
                          fontWeight: "bold",
                          fontSize: "1rem",
                          height: 32,
                          bgcolor: dashboardData.userEngagement.bounceRate > 50 ? "#ffebee" : "#fff3e0",
                          color: dashboardData.userEngagement.bounceRate > 50 ? "#d32f2f" : "#ed6c02",
                          border: `1px solid ${dashboardData.userEngagement.bounceRate > 50 ? "#d32f2f" : "#ed6c02"}`
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                      Users registered in last 30 days who are inactive
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={dashboardData.userEngagement.bounceRate} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: "#ffebee",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: dashboardData.userEngagement.bounceRate > 50 ? "#d32f2f" : "#ed6c02",
                          borderRadius: 4
                        }
                      }} 
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* <Grid item xs={12} md={6}>
            <Card className={styles.analyticsCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Content Performance
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <LikesIcon color="primary" sx={{ mr: 1 }} />
                    <Box flex={1}>
                      <Typography variant="body2">Total Likes</Typography>
                      <Typography variant="h6" fontWeight="bold">2,450</Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={2}>
                    <CommentsIcon color="secondary" sx={{ mr: 1 }} />
                    <Box flex={1}>
                      <Typography variant="body2">Total Comments</Typography>
                      <Typography variant="h6" fontWeight="bold">1,230</Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center">
                    <ViewsIcon color="success" sx={{ mr: 1 }} />
                    <Box flex={1}>
                      <Typography variant="body2">Total Views</Typography>
                      <Typography variant="h6" fontWeight="bold">12,500</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid> */}
        </Grid>
      </Box>
    </Layout>
  );
};

export default Dashboard; 